import * as THREE from "three";
import Experience from "../Experience.js";

export default class Walls {
  constructor(_wallsTextures) {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.room = this.experience.room;
    this.resources = this.experience.resources;
    this.camera = this.experience.camera;
    this.sizes = this.experience.sizes;
    this.objectManager = this.experience.objectManager;
    this.textureLoader = this.experience.textureLoader;
    this.controls = this.camera.controls;
    this.sizeScene = this.experience.sizeScene;
    this.time = this.experience.time;
    this.menuManager = this.experience.menuManager;
    this.saveRoom = this.room.saveRoom;

    this.shapeRoom = this.room.shape;
    this.measureWalls = this.room.measureWalls;
    this.heightWalls = this.room.heightWalls;

    this.wallsTextures = _wallsTextures;

    this.wallThickness = 0;

    this.wallsMaterial = [];

    this.walls = [];
    this.roomBoundingBox = null;
    this.maxDistance = 8;
    this.boxHelper = null;

    this.createWalls(this.measureWalls, this.heightWalls, this.shapeRoom);

    this.isDragging = false;
    this.mouse = new THREE.Vector2();
    this.initialPosition = new THREE.Vector3();
    this.offset = new THREE.Vector3();

    this.pointer = new THREE.Vector2(Infinity, Infinity);
    this.raycaster = this.experience.raycaster;
  }

  /**
   * Create walls
   */
  createWalls(sizesWalls, height, shapeType) {
    const wallHeight = height;

    // Définir les segments en fonction de la forme
    if (shapeType === "rectangle") {
      this.wallSegments = [
        new THREE.Vector3(-this.wallThickness, 0, -this.wallThickness),
        new THREE.Vector3(
          sizesWalls[0] + this.wallThickness,
          0,
          -this.wallThickness
        ),
        new THREE.Vector3(
          sizesWalls[0] + this.wallThickness,
          0,
          sizesWalls[1] + this.wallThickness
        ),
        new THREE.Vector3(
          -this.wallThickness,
          0,
          sizesWalls[1] + this.wallThickness
        ),
      ];
    } else if (shapeType === "L") {
      const orientation = sizesWalls[4];

      const isLeft = orientation === "left"; // Vérifie si l'orientation est à gauche

      this.wallSegments = !isLeft
        ? // L tourné vers la droite
          [
            new THREE.Vector3(-this.wallThickness, 0, -this.wallThickness),
            new THREE.Vector3(
              sizesWalls[0] + this.wallThickness,
              0,
              -this.wallThickness
            ),
            new THREE.Vector3(
              sizesWalls[0] + this.wallThickness,
              0,
              sizesWalls[2] + this.wallThickness
            ),
            new THREE.Vector3(
              sizesWalls[0] - sizesWalls[1] + this.wallThickness,
              0,
              sizesWalls[2] + this.wallThickness
            ),
            new THREE.Vector3(
              sizesWalls[0] - sizesWalls[1] + this.wallThickness,
              0,
              sizesWalls[3] + this.wallThickness
            ),
            new THREE.Vector3(
              this.wallThickness,
              0,
              sizesWalls[3] + this.wallThickness
            ),
          ]
        : [
            // L tourné vers la gauche
            new THREE.Vector3(-this.wallThickness, 0, -this.wallThickness),
            new THREE.Vector3(
              sizesWalls[0] + this.wallThickness,
              0,
              -this.wallThickness
            ),
            new THREE.Vector3(
              sizesWalls[0] + this.wallThickness,
              0,
              sizesWalls[3] + this.wallThickness
            ),
            new THREE.Vector3(
              sizesWalls[1] + this.wallThickness,
              0,
              sizesWalls[3] + this.wallThickness
            ),
            new THREE.Vector3(
              sizesWalls[1] + this.wallThickness,
              0,
              sizesWalls[2] + this.wallThickness
            ),
            new THREE.Vector3(
              -this.wallThickness,
              0,
              sizesWalls[2] + this.wallThickness
            ),
          ];
    } else if (shapeType === "T") {
      this.wallSegments = [
        new THREE.Vector3(-this.wallThickness, 0, -this.wallThickness), // 0
        new THREE.Vector3(
          sizesWalls[0] + this.wallThickness,
          0,
          -this.wallThickness
        ), // point A
        new THREE.Vector3(
          sizesWalls[0] + this.wallThickness,
          0,
          sizesWalls[1] + this.wallThickness
        ), // point B
        new THREE.Vector3(
          sizesWalls[0] + sizesWalls[2] + this.wallThickness,
          0,
          sizesWalls[1] + this.wallThickness
        ), // point C
        new THREE.Vector3( // point D
          sizesWalls[0] + sizesWalls[2] + this.wallThickness,
          0,
          sizesWalls[1] + sizesWalls[3] + this.wallThickness * 2
        ),
        new THREE.Vector3( // point E
          sizesWalls[0] + sizesWalls[2] - sizesWalls[4] - this.wallThickness,
          0,
          sizesWalls[1] + sizesWalls[3] + this.wallThickness * 2
        ),
        new THREE.Vector3(
          sizesWalls[0] + sizesWalls[2] - sizesWalls[4] - this.wallThickness,
          0,
          sizesWalls[1] + this.wallThickness
        ), // point F
        new THREE.Vector3(
          0 - this.wallThickness,
          0,
          sizesWalls[1] + this.wallThickness
        ), // G
        /* new THREE.Vector3(0 - this.wallThickness, 0, - this.wallThickness), */ // Origine
      ];
    } else {
      console.error("Forme non supportée");
      return;
    }

    this.wallsGroup = new THREE.Group();
    this.planeGroup = new THREE.Group();
    const maxX = Math.max(...this.wallSegments.map((point) => point.x));
    const maxZ = Math.max(...this.wallSegments.map((point) => point.z));

    // Création des murs
    for (let i = 0; i < this.wallSegments.length; i++) {
      const start = this.wallSegments[i];
      const end = this.wallSegments[(i + 1) % this.wallSegments.length];

      const wallLength = new THREE.Vector3().subVectors(end, start).length();
      const wallDirection = new THREE.Vector3()
        .subVectors(end, start)
        .normalize();

      const wallGeometry = new THREE.BoxGeometry(
        wallLength,
        wallHeight,
        this.wallThickness
      );

      this.normalTexture = this.textureLoader.load(
        "./products/wall/normalMap.jpg"
      );

      this.roughnessMap = this.textureLoader.load(
        "./products/wall/roughtMap.png"
      );

      this.normalTexture.repeat.set(wallLength / 3.5, 0.85);
      this.normalTexture.wrapS = THREE.RepeatWrapping;
      this.normalTexture.wrapT = THREE.RepeatWrapping;

      this.roughnessMap.repeat.set(wallLength / 2.4, 0.8);
      this.roughnessMap.wrapS = THREE.RepeatWrapping;
      this.roughnessMap.wrapT = THREE.RepeatWrapping;

      const wallMaterial = [
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
        new THREE.MeshStandardMaterial({
          color: 0xffffff,
          normalMap: this.normalTexture,
          roughness: 0.8,
          // roughnessMap: this.roughnessMap,
        }), // intérieur
        new THREE.MeshStandardMaterial({ color: 0xffffff }),
      ];

      // Ajoutez le matériau dans le tableau

      this.wallsMaterial.push(wallMaterial);

      // Créez le mesh avec le matériau
      const wallMesh = new THREE.Mesh(wallGeometry, wallMaterial);

      const midpoint = new THREE.Vector3()
        .addVectors(start, end)
        .divideScalar(2);

      wallMesh.userData.wallId = this.wallsMaterial.length - 1;

      // If textures specified : change texture of walls
      if (this.wallsTextures && this.wallsTextures.length > 0) {
        const texture = this.wallsTextures[wallMesh.userData.wallId];

        this.changeWallTexture(texture, wallMesh);
      }

      wallMesh.position.copy(midpoint);
      wallMesh.position.y = wallHeight / 2;
      const angle = Math.atan2(wallDirection.z, wallDirection.x);
      wallMesh.rotation.y = -angle;

      const boxWall = new THREE.Box3().setFromObject(wallMesh);

      // Plane for snapping object to wall
      const planeGeometry = new THREE.PlaneGeometry(wallLength, wallHeight);
      const planeMaterial = new THREE.MeshBasicMaterial({
        side: THREE.DoubleSide,
        opacity: 0,
        transparent: true,
      });
      const plane = new THREE.Mesh(planeGeometry, planeMaterial);
      plane.position.copy(midpoint);
      plane.position.y = wallHeight / 2;
      plane.rotation.y = -angle;

      this.planeGroup.add(plane);

      // End Plane for snapping object to wall

      /** Remplir les coins */
      if (shapeType === "L") {
        if (i === 0) {
          wallMesh.position.z += this.wallThickness / 2;
        } else if (i === 1) {
          wallMesh.position.x -= this.wallThickness / 2;
        } else if (i === 2) {
          wallMesh.position.z -= this.wallThickness / 2;
        } else if (i === 3) {
          wallMesh.position.x += this.wallThickness / 2;
        } else if (i === 4) {
          wallMesh.position.z -= this.wallThickness / 2;
          wallMesh.position.x += this.wallThickness;

          let boxSize = new THREE.Vector3();
          const sizeMesh = boxWall.getSize(boxSize);

          const proportionX = sizeMesh.x * this.wallThickness;

          wallMesh.scale.x = 1 + proportionX / 1000;
        } else if (i === 5) {
          wallMesh.position.x += this.wallThickness / 2;
        }
      } else if (shapeType === "rectangle") {
        if (i === 0) {
          wallMesh.position.z += this.wallThickness / 2;
        } else if (i === 1) {
          wallMesh.position.x -= this.wallThickness / 2;
        } else if (i === 2) {
          wallMesh.position.z -= this.wallThickness / 2;
        } else if (i === 3) {
          wallMesh.position.x += this.wallThickness / 2;
        }
      } else if (shapeType === "T") {
        if (i === 0) {
          wallMesh.position.z += this.wallThickness / 2;
        } else if (i === 1) {
          wallMesh.position.x -= this.wallThickness / 2;
        } else if (i === 2) {
          wallMesh.position.z += this.wallThickness / 2;
          wallMesh.position.x -= this.wallThickness;

          let boxSize = new THREE.Vector3();
          const sizeMesh = boxWall.getSize(boxSize);

          const proportionX = sizeMesh.x * this.wallThickness;

          wallMesh.scale.x = 1 + proportionX / 1000;
        } else if (i === 3) {
          wallMesh.position.x -= this.wallThickness / 2;
        } else if (i === 4) {
          wallMesh.position.z -= this.wallThickness / 2;
        } else if (i === 5) {
          wallMesh.position.x += this.wallThickness / 2;
        } else if (i === 6) {
          wallMesh.position.z += this.wallThickness / 2;
          wallMesh.position.x += this.wallThickness;
          let boxSize = new THREE.Vector3();
          const sizeMesh = boxWall.getSize(boxSize);

          const proportionX = sizeMesh.x * this.wallThickness;

          wallMesh.scale.x = 1 + proportionX / 1000;
        } else if (i === 7) {
          wallMesh.position.x += this.wallThickness / 2;
        }
      }

      this.wallsGroup.position.x = 0 - maxX / 2;
      this.wallsGroup.position.z = 0 - maxZ / 2;
      this.wallsGroupPosition = this.wallsGroup.position;
      wallMesh.name = "wall";

      if (this.objectManager !== undefined) {
        this.objectManager.addObjectCollision(wallMesh);
      }
      wallMesh.receiveShadow = true;

      this.wallsGroup.add(wallMesh);

      this.walls.push(wallMesh);
    }
    this.wallsGroup.name = "wallsGroup";
    this.scene.add(this.wallsGroup);

    // Position Plane Group
    this.planeGroup.position.x = -maxX / 2 + this.wallThickness / 2;
    this.planeGroup.position.z = -maxZ / 2 + this.wallThickness / 2;

    const scaleFactor = 1 - this.wallThickness / Math.max(maxX, maxZ);
    this.planeGroup.scale.set(scaleFactor, 1, scaleFactor);

    this.planeGroup.name = "planeGroupWall";
    this.scene.add(this.planeGroup);

    this.roomBoundingBox = new THREE.Box3().setFromObject(this.wallsGroup);

    const roomCenter = this.calculateRoomCenter();
    this.controls.target.copy(new THREE.Vector3(0, 1, 0));
    this.controls.update();
  }

  isCameraInsideRoom() {
    return this.roomBoundingBox.containsPoint(this.camera.instance.position);
  }

  /**
   * Raycaster dectect and hide wall near and front of camera
   */
updateRaycasterHideWall() {
  if (this.isCameraInsideRoom() || this.camera.is2DView) {
    this.walls.forEach(wall => wall.visible = true);
    return;
  }

  const camera = this.camera.instance;
  const cameraPosition = camera.position.clone();

  const cameraDirection = new THREE.Vector3();
  camera.getWorldDirection(cameraDirection);

  // Tout afficher au départ
  this.walls.forEach(wall => wall.visible = true);

  const maxHideDistance = 10;
  const wallsInFront = [];

  this.walls.forEach(wall => {
    // Centre du mur
    const wallPosition = new THREE.Vector3();
    wall.getWorldPosition(wallPosition);

    // Direction caméra → mur
    const dirToWall = wallPosition.clone().sub(cameraPosition);
    const distance = dirToWall.length();
    dirToWall.normalize();

    // Produit scalaire : > 0 = devant la caméra
    const dot = dirToWall.dot(cameraDirection);

    if (dot > 0.3 && distance < maxHideDistance) {
      wallsInFront.push({
        wall,
        distance
      });
    }
  });

  // Trier par distance (les plus proches d'abord)
  wallsInFront.sort((a, b) => a.distance - b.distance);

  // Cacher les 2 murs les plus proches
  for (let i = 0; i < Math.min(2, wallsInFront.length); i++) {
    wallsInFront[i].wall.visible = false;
  }
}


  /**
   * Calculate pivot point
   */
  calculateRoomCenter() {
    const center = new THREE.Vector3(0, 0, 0);
    this.wallSegments.forEach((point) => {
      center.add(point);
    });
    center.divideScalar(this.wallSegments.length);
    return center;
  }

  /**
   * Click on a wall (for change the texture)
   */
  onPointerDown(event) {
    if (event.target.closest(".protected")) {
      return; // Ignorer les clics sur les boutons dans la scène
    }

    if (this.room.getIsSelectedObject()) {
      return; // Prioriser les objets sélectionnés
    }

    this.pointerDownTime = Date.now();

    // Normalisation du pointeur
    const normalizedPointer = this.experience.normalizePointer(event);
    this.pointer.x = normalizedPointer.x;
    this.pointer.y = normalizedPointer.y;
  }

  onPointerUp(event) {
    const clickDuration = Date.now() - this.pointerDownTime;

    // Gérer uniquement les clics courts (< 200ms)
    if (clickDuration >= 200) {
      return;
    }

    // Configurer le raycaster ici pour déterminer l'objet cliqué
    this.raycaster.setFromCamera(this.pointer, this.camera.instance);

    const visibleWalls = this.walls.filter((wall) => wall.visible);

    const intersects = this.raycaster.intersectObjects(visibleWalls, true);
    // Prioriser la logique des objets sélectionnés

    if (this.room.getIsSelectedObject()) {
      return;
    }
    if (intersects.length > 0) {
      const clickedObject = intersects[0].object;

      this.currentClickedObject = clickedObject;

      // Ajout d'un BoxHelper pour visualiser la sélection
      if (this.boxHelper) {
        this.scene.remove(this.boxHelper); // Supprime l'ancien helper
      }
      if (!this.camera.is2DView) {
        this.boxHelper = new THREE.BoxHelper(
          this.currentClickedObject,
          0xff0000
        );
        this.scene.add(this.boxHelper);
      }

      // Ouvrir le menu de changement de texture
      this.menuManager.openContainerChangeTexture("wall");
    } else {
      // Si aucun mur n'est cliqué, fermer le menu
      this.detachSelectedWall();
      /* if(!this.camera.is2DView){
        this.menuManager.closeContainerAboveMenu();
      } */
    }

    // Réinitialisation pour éviter les erreurs
    this.pointerDownTime = 0;
  }

  getWallSegments() {
    return this.wallSegments;
  }

  getWallsGroupPosition() {
    return this.wallsGroupPosition;
  }

  getWallsGroup() {
    return this.wallsGroup;
  }

  update() {
    this.updateRaycasterHideWall();
  }

  getIsSelectedWall() {
    return this.currentClickedObject ? true : false;
  }

  setSelectedWall(value) {
    this.currentClickedObject = value;
  }

  detachSelectedWall() {
    if (this.currentClickedObject) {
      this.scene.remove(this.boxHelper);
      this.currentClickedObject = null;
    }
  }

  /**
   * Set texture of a wall
   */
  changeWallTexture(textureUrl, object) {
    // Check if a wall is selected
    if (!object) {
      console.warn("Aucun mur sélectionné !");
      return;
    }

    this.textureLoader.load(
      textureUrl,
      (texture) => {
        const targetWallIndex = object.userData.wallId;

        const wallMaterial = this.wallsMaterial[targetWallIndex];

        this.wallsTextures[targetWallIndex] = textureUrl;

        this.saveRoom.setTexturesWalls(this.wallsTextures);

        if (wallMaterial && wallMaterial[4]) {
          wallMaterial[4].color = null;
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          wallMaterial[4].map = texture;
          wallMaterial[4].needsUpdate = true;
        } else {
          console.error(
            `Impossible d'appliquer la texture : matériau ou face manquants pour le mur ${targetWallIndex}.`
          );
        }
      },
      undefined,
      (error) => {
        console.error("Erreur lors du chargement de la texture :", error);
      }
    );
  }

  /**
   * Change the texture of all walls
   */
  changeAllWallTextures(textureUrl) {
    // Load texture
    this.textureLoader.load(
      textureUrl,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;

        this.walls.forEach((wall) => {
          const targetWallIndex = wall.userData.wallId;
          const wallMaterial = this.wallsMaterial[targetWallIndex];
          this.wallsTextures[targetWallIndex] = textureUrl;

          if (wallMaterial && wallMaterial[4]) {
            wallMaterial[4].color = null; // Delete default color
            wallMaterial[4].map = texture; // Apply texture
            wallMaterial[4].needsUpdate = true;
          } else {
            console.error(
              `Impossible d'appliquer la texture : matériau ou face manquants pour le mur ${targetWallIndex}.`
            );
          }
        });
        this.saveRoom.setTexturesWalls(this.wallsTextures);
      },
      undefined,
      (error) => {
        console.error("Erreur lors du chargement de la texture :", error);
      }
    );
  }

  /**
   * Delete walls
   */
  deleteWalls() {
    // Supprimer les murs de la scène
    this.walls.forEach((wall) => {
      this.scene.remove(wall);
      wall.geometry.dispose();
      if (wall.material) {
        wall.material.forEach((mat) => {
          if (mat.map) mat.map.dispose();
          mat.dispose();
        });
      }
    });

    // Supprimer le groupe de murs et réinitialiser les références
    if (this.wallsGroup) {
      this.scene.remove(this.wallsGroup);
      this.wallsGroup.children.forEach((child) => {
        if (child.geometry) {
          // Retirer des objets de collision
          this.objectManager.removeObjectCollision(child);
          child.geometry.dispose();
        }
        if (child.material) {
          child.material.forEach((mat) => {
            if (mat.map) mat.map.dispose();
            mat.dispose();
          });
        }
      });
    }

    // Supprimer le groupe de plan de détection (planeGroup)
    if (this.planeGroup) {
      this.scene.remove(this.planeGroup);
      this.planeGroup.children.forEach((child) => {
        if (child.geometry) child.geometry.dispose();
      });
    }

    // Réinitialiser les variables
    this.walls = [];
    this.wallsGroup = new THREE.Group();
    this.planeGroup = new THREE.Group();
    this.scene.remove(this.wallsGroup);
    this.scene.remove(this.planeGroup);

    // Remettre les groupes à zéro
    this.wallsGroup.children = [];
    this.planeGroup.children = [];
  }

  getWallThickness() {
    return this.wallThickness;
  }
}
